import React, { useEffect, useState } from 'react';
import { useSettingsHandlers } from '@/presentation/providers/settings-handlers-provider';
import { getContainerHeight, makeInvalidFormErrorMessage } from '@/presentation/common-utils/common-utils';
import { Button, Container, Form, Header, SpaceBetween, FormField, TextContent, Icon, Box, } from '@cloudscape-design/components-themed/components';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/presentation/providers/notifications-provider';
import { getOrgIDFromToken } from '@/lib/auth0/utils';
import { useAuth0 } from '@/lib/auth0';
import { makeNotification } from '@/presentation/pages';
import { PagesRequestErrorHandler } from '@/presentation/pages/errors';
import CodeEditor from "@/presentation/components/code-editor/code-editor";
import { useIsMounted } from '@/presentation/hooks';
import { useAppContext } from "@/presentation/providers/app-context-provider";
import { PATHS } from '@/presentation/common-utils/constants';
import { SETTINGS_TABS } from '../settings-types';
const isPlainObject = (val) => typeof val === 'object' && val !== null && !Array.isArray(val);
const VALID_PROVIDERS = new Set(['AWS']);
const VALID_ACTION_KEYS = new Set(['IsCreate', 'IsDataPlane', 'Taggable']);
const validateActionProps = (path, props) => {
    if (!isPlainObject(props))
        return `"${path}" must be an object`;
    for (const [key, val] of Object.entries(props)) {
        if (!VALID_ACTION_KEYS.has(key)) {
            return `"${path}" contains invalid property "${key}"`;
        }
        if (typeof val !== 'boolean') {
            return `"${path}.${key}" must be a boolean`;
        }
    }
    return null;
};
const validateServiceActions = (path, actions) => {
    if (!isPlainObject(actions))
        return `"${path}" must be an object`;
    for (const [action, props] of Object.entries(actions)) {
        const error = validateActionProps(`${path}.${action}`, props);
        if (error)
            return error;
    }
    return null;
};
const validateProviderServices = (provider, services) => {
    if (!isPlainObject(services))
        return `"${provider}" must be an object`;
    for (const [service, actions] of Object.entries(services)) {
        const error = validateServiceActions(`${provider}.${service}`, actions);
        if (error)
            return error;
    }
    return null;
};
const validateMetadataSchema = (data) => {
    if (!isPlainObject(data))
        return 'Root must be an object';
    for (const [provider, services] of Object.entries(data)) {
        if (!VALID_PROVIDERS.has(provider)) {
            return `"${provider}" is not a valid provider. Valid providers: ${[...VALID_PROVIDERS].join(', ')}`;
        }
        const error = validateProviderServices(provider, services);
        if (error)
            return error;
    }
    return null;
};
const SettingsActionMetadataOverridesEdit = () => {
    const [editorLoading, setEditorLoading] = useState(true);
    const [editorHeight, setEditorHeight] = useState(400);
    const [currentRef, setCurrentRef] = useState(null);
    const [metadataValue, setMetadataValue] = useState('{}');
    const [constraintText, setConstraintText] = useState(null);
    const [errorText, setErrorText] = useState(null);
    const [orgID, setOrgID] = useState(0);
    const [actionMetadataOverrideId, setActionMetadataOverrideId] = useState(null);
    const ref = React.useRef(null);
    const errorRef = React.useRef(null);
    const isMounted = useIsMounted();
    const { pushNotifications, dismissNotification } = useNotifications();
    const { settings } = useSettingsHandlers();
    const settingsHandler = settings;
    const navigate = useNavigate();
    const { user, rawToken } = useAuth0();
    const { setIsFormDirty, setDirtyFormResourceName } = useAppContext();
    const navigateUrl = `${PATHS.SETTINGS}?tabId=${SETTINGS_TABS.ACTION_METADATA_OVERRIDES}`;
    const updateEditorHeight = (updatedHeight) => {
        var _a;
        const removeCreateButtonDivSize = 100;
        // Approximate padding size around the create button container in pixels
        const currentErrorHeight = ((_a = document.getElementById("editorCustomErrorContent")) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0;
        const updatedErrorContainerHeight = Math.min(currentErrorHeight, 100);
        const newHeight = updatedHeight - removeCreateButtonDivSize - updatedErrorContainerHeight;
        setEditorHeight(newHeight);
    };
    useEffect(() => {
        setCurrentRef(ref === null || ref === void 0 ? void 0 : ref.current);
    }, [ref === null || ref === void 0 ? void 0 : ref.current]);
    useEffect(() => {
        // The DOM element is accessible here.
        if (currentRef) {
            getContainerHeight('settingsEditActionMetadataOverridesEditor', updateEditorHeight, editorHeight);
        }
    }, [currentRef]);
    useEffect(() => {
        if ((constraintText || errorText) && errorRef) {
            getContainerHeight('settingsEditActionMetadataOverridesEditor', updateEditorHeight, editorHeight);
        }
    }, [errorRef === null || errorRef === void 0 ? void 0 : errorRef.current, errorText, constraintText]);
    useEffect(() => {
        if (isMounted.current) {
            settingsHandler.listActionMetadataOverrides().then((actionMetadata) => {
                var _a;
                if (isMounted.current) {
                    const { metadata, id } = ((_a = actionMetadata === null || actionMetadata === void 0 ? void 0 : actionMetadata.ActionMetadataOverrides) === null || _a === void 0 ? void 0 : _a[0]) || {};
                    if (id) {
                        setActionMetadataOverrideId(id);
                        setMetadataValue(metadata ? JSON.stringify(metadata, null, 2) : '{}');
                    }
                }
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                if (isMounted.current)
                    setEditorLoading(false);
            });
        }
    }, []);
    useEffect(() => {
        setOrgID(getOrgIDFromToken(rawToken));
    }, [user]);
    const makeSuccessNotification = () => {
        const msg = 'Action metadata overrides updated successfully';
        return makeNotification("success", msg, dismissNotification);
    };
    const makeErrorNotification = (message) => {
        const msg = 'Unable to proceed with this request. Please try again later.';
        return makeNotification("error", message || msg, dismissNotification);
    };
    const handleErrorResponse = (response) => {
        const getResponseMessage = PagesRequestErrorHandler(response);
        pushNotifications(makeErrorNotification(getResponseMessage === null || getResponseMessage === void 0 ? void 0 : getResponseMessage.errorMessage));
    };
    const hanldeDirtyFormChange = (value = true) => {
        setIsFormDirty(value);
        setDirtyFormResourceName(value ? 'metadata' : null);
    };
    const handleOnSubmit = () => {
        const parsedMetadata = metadataValue === "" ? {} : JSON.parse(metadataValue);
        const isUpdate = !!actionMetadataOverrideId;
        const request = isUpdate
            ? settingsHandler.updateActionMetadataOverrides({ id: actionMetadataOverrideId, metadata: parsedMetadata })
            : settingsHandler.createActionMetadataOverrides({ org_id: orgID, metadata: parsedMetadata });
        const isSuccess = (result) => {
            var _a, _b;
            return isUpdate
                ? ((_a = result === null || result === void 0 ? void 0 : result.update_ActionMetadataOverrides_by_pk) === null || _a === void 0 ? void 0 : _a.id) === actionMetadataOverrideId
                : ((_b = result === null || result === void 0 ? void 0 : result.insert_ActionMetadataOverrides_one) === null || _b === void 0 ? void 0 : _b.id) !== undefined;
        };
        request
            .then((result) => {
            if (isSuccess(result)) {
                pushNotifications(makeSuccessNotification());
                hanldeDirtyFormChange(false);
                navigate(navigateUrl);
            }
            else {
                pushNotifications(makeErrorNotification(`Error ${isUpdate ? 'updating' : 'creating'} action metadata overrides`));
            }
        })
            .catch(({ response }) => handleErrorResponse(response));
    };
    const handleCodeChange = (jsonCode) => {
        setMetadataValue(jsonCode);
        hanldeDirtyFormChange(true);
    };
    const validateMetadata = (isSubmit = false) => {
        if (!isMounted.current)
            return;
        setEditorLoading(true);
        try {
            const parsed = JSON.parse(metadataValue);
            const error = validateMetadataSchema(parsed);
            if (error) {
                setConstraintText(null);
                setErrorText(React.createElement("pre", null, error));
                if (isSubmit) {
                    pushNotifications(makeErrorNotification(makeInvalidFormErrorMessage('action metadata override', 'update')));
                }
            }
            else if (isSubmit) {
                handleOnSubmit();
            }
            else {
                setErrorText(null);
                setConstraintText(React.createElement(TextContent, null,
                    React.createElement("p", null,
                        React.createElement(Icon, { name: "status-positive", variant: "success" }),
                        " ",
                        React.createElement("strong", null,
                            React.createElement("small", null, "Action metadata is valid")))));
            }
        }
        catch (e) {
            setConstraintText(null);
            setErrorText(React.createElement("pre", null, e instanceof Error ? e.message : 'Invalid JSON'));
            if (isSubmit) {
                pushNotifications(makeErrorNotification(makeInvalidFormErrorMessage('action metadata override', 'update')));
            }
        }
        finally {
            setEditorLoading(false);
        }
    };
    return React.createElement("div", { className: 'form-container' },
        React.createElement(Form, { actions: React.createElement(SpaceBetween, { direction: "horizontal", size: "xs" },
                React.createElement(Button, { formAction: "none", variant: "link", onClick: () => navigate(navigateUrl) }, "Cancel"),
                React.createElement(Button, { variant: "primary", onClick: () => {
                        if (metadataValue === '') {
                            handleOnSubmit();
                        }
                        else {
                            validateMetadata(true);
                        }
                    } }, "Update")) },
            React.createElement(Container, { header: React.createElement(Header, { variant: "h2", actions: React.createElement(SpaceBetween, { direction: "horizontal", size: "l" },
                        React.createElement(Button, { variant: "normal", disabled: !metadataValue, onClick: () => validateMetadata() }, "Validate")) }, "Overrides") },
                React.createElement(SpaceBetween, { direction: "vertical", size: "l" },
                    React.createElement("div", { ref: ref, id: "settingsEditActionMetadataOverridesEditor" },
                        React.createElement(FormField, null,
                            React.createElement(CodeEditor, { loading: editorLoading, code: metadataValue, onChange: handleCodeChange, height: editorHeight, heightType: 'px', readOnly: false, language: 'json' }),
                            React.createElement("div", { style: { maxHeight: '100px', overflow: "auto", marginTop: '10px' } },
                                React.createElement(Box, { float: "left", color: "text-status-error" },
                                    React.createElement("div", { className: "flex" },
                                        errorText && (React.createElement("div", { className: "mr-2" },
                                            React.createElement(Icon, { name: "status-warning", size: "small", variant: "error" }))),
                                        React.createElement("div", { ref: errorRef, id: "editorCustomErrorContent" }, errorText || constraintText))))))))));
};
export default SettingsActionMetadataOverridesEdit;
